import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Form,
  Input,
  List,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuth } from '@/features/auth/auth-context';
import { useActiveOrg } from '../active-org-context';
import { organizationsApi } from '../organizations.api';
import type { InvitableRole, Member, OrgRole } from '../types';

const inviteSchema = z.object({
  email: z.string().trim().email('Enter a valid email'),
  role: z.enum(['admin', 'member']),
});
type InviteForm = z.infer<typeof inviteSchema>;

const ROLE_COLORS: Record<OrgRole, string> = { owner: 'gold', admin: 'blue', member: 'default' };

export function MembersPage() {
  const { message } = App.useApp();
  const { user } = useAuth();
  const { activeOrg } = useActiveOrg();
  const queryClient = useQueryClient();

  const orgId = activeOrg?.id ?? '';
  const isOwner = activeOrg?.role === 'owner';
  const isAdmin = activeOrg?.role === 'owner' || activeOrg?.role === 'admin';

  const membersKey = ['members', orgId] as const;
  const invitesKey = ['invitations', orgId] as const;

  const membersQuery = useQuery({
    queryKey: membersKey,
    queryFn: () => organizationsApi.listMembers(orgId),
    enabled: Boolean(orgId),
  });
  const invitesQuery = useQuery({
    queryKey: invitesKey,
    queryFn: () => organizationsApi.listInvitations(orgId),
    enabled: Boolean(orgId) && isAdmin,
  });

  const invalidateMembers = () => queryClient.invalidateQueries({ queryKey: membersKey });
  const invalidateInvites = () => queryClient.invalidateQueries({ queryKey: invitesKey });

  const inviteForm = useForm<InviteForm>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'member' },
  });

  const inviteMutation = useMutation({
    mutationFn: (values: InviteForm) => organizationsApi.createInvitation(orgId, values),
    onSuccess: () => {
      void invalidateInvites();
      inviteForm.reset();
      message.success('Invitation sent');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to invite'),
  });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: OrgRole }) =>
      organizationsApi.updateMemberRole(orgId, userId, role),
    onSuccess: () => {
      void invalidateMembers();
      message.success('Role updated');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to update role'),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => organizationsApi.removeMember(orgId, userId),
    onSuccess: () => {
      void invalidateMembers();
      message.success('Member removed');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to remove member'),
  });

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => organizationsApi.revokeInvitation(orgId, invitationId),
    onSuccess: () => {
      void invalidateInvites();
      message.success('Invitation revoked');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to revoke'),
  });

  if (!activeOrg) {
    return <Typography.Paragraph>Select an organization to manage members.</Typography.Paragraph>;
  }

  const canRemove = (m: Member): boolean => {
    if (m.userId === user?.id) return false; // use "leave" for self
    if (!isAdmin) return false;
    return isOwner || m.role === 'member';
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ marginBottom: 0 }}>
        {activeOrg.name} · Members
      </Typography.Title>

      {isAdmin ? (
        <Card title="Invite a member">
          <Form
            layout="inline"
            onFinish={inviteForm.handleSubmit((v) => inviteMutation.mutate(v))}
            style={{ rowGap: 12 }}
          >
            <Controller
              control={inviteForm.control}
              name="email"
              render={({ field, fieldState }) => (
                <Form.Item
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message}
                >
                  <Input {...field} placeholder="teammate@example.com" style={{ width: 260 }} />
                </Form.Item>
              )}
            />
            <Controller
              control={inviteForm.control}
              name="role"
              render={({ field }) => (
                <Form.Item>
                  <Select<InvitableRole>
                    {...field}
                    style={{ width: 130 }}
                    options={[
                      { value: 'member', label: 'Member' },
                      { value: 'admin', label: 'Admin' },
                    ]}
                  />
                </Form.Item>
              )}
            />
            <Button type="primary" htmlType="submit" loading={inviteMutation.isPending}>
              Send invite
            </Button>
          </Form>
        </Card>
      ) : null}

      <Card title="Members">
        <Table<Member>
          rowKey="userId"
          loading={membersQuery.isLoading}
          dataSource={membersQuery.data ?? []}
          pagination={false}
          columns={[
            {
              title: 'Name',
              dataIndex: 'name',
              render: (name: string, m) => (
                <span>
                  {name} {m.userId === user?.id ? <Tag>You</Tag> : null}
                </span>
              ),
            },
            { title: 'Email', dataIndex: 'email' },
            {
              title: 'Role',
              dataIndex: 'role',
              render: (role: OrgRole, m) =>
                isOwner ? (
                  <Select<OrgRole>
                    value={role}
                    style={{ width: 120 }}
                    loading={roleMutation.isPending}
                    onChange={(newRole) => roleMutation.mutate({ userId: m.userId, role: newRole })}
                    options={[
                      { value: 'owner', label: 'Owner' },
                      { value: 'admin', label: 'Admin' },
                      { value: 'member', label: 'Member' },
                    ]}
                  />
                ) : (
                  <Tag color={ROLE_COLORS[role]}>{role}</Tag>
                ),
            },
            {
              title: '',
              key: 'actions',
              align: 'right',
              render: (_: unknown, m) =>
                canRemove(m) ? (
                  <Popconfirm
                    title="Remove this member?"
                    onConfirm={() => removeMutation.mutate(m.userId)}
                  >
                    <Button danger size="small">
                      Remove
                    </Button>
                  </Popconfirm>
                ) : null,
            },
          ]}
        />
      </Card>

      {isAdmin ? (
        <Card title="Pending invitations">
          <List
            loading={invitesQuery.isLoading}
            dataSource={invitesQuery.data ?? []}
            locale={{ emptyText: 'No pending invitations' }}
            renderItem={(invite) => (
              <List.Item
                actions={[
                  <Popconfirm
                    key="revoke"
                    title="Revoke this invitation?"
                    onConfirm={() => revokeMutation.mutate(invite.id)}
                  >
                    <Button size="small">Revoke</Button>
                  </Popconfirm>,
                ]}
              >
                <List.Item.Meta
                  title={invite.email}
                  description={
                    <Space>
                      <Tag color={ROLE_COLORS[invite.role]}>{invite.role}</Tag>
                      <span>expires {new Date(invite.expiresAt).toLocaleDateString()}</span>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        </Card>
      ) : null}
    </Space>
  );
}
